import { editChallengeHolder } from '@actions/challengeGroups/editChallengeHolder';
import { fetchChallengeHoldersForGroup } from '@actions/challengeGroups/fetchChallengeHoldersForGroup';
import {
    getChallengeGroupsFetchError,
    getChallengeGroupsIsFetching,
    getChallengeGroupsIsPosting,
    getChallengeGroupsPostError,
    getChallengeGroupsPostSuccess,
    getChallengeHolders,
} from '@selectors/challengeGroups';
import useForm from '@components/form/hooks/useForm';
import usePrevious from 'lib/src/hooks/usePrevious';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChallengeGroup } from 'src/types/shared/ChallengeGroup';

const useEditChallengeHolder = (challengeGroup: ChallengeGroup, hideModal: () => void): Hook => {
    const dispatch = useDispatch();

    const isFetching = useSelector(getChallengeGroupsIsFetching);
    const fetchError = useSelector(getChallengeGroupsFetchError);
    const isPosting = useSelector(getChallengeGroupsIsPosting);
    const postError = useSelector(getChallengeGroupsPostError);
    const holders = useSelector(getChallengeHolders);
    const postSuccess = useSelector(getChallengeGroupsPostSuccess);
    const prevProps = usePrevious({
        isFetching,
        postSuccess,
        challengeGroup,
        holders,
    });

    const [formState, handleChange] = useForm({
        challengeHolderIDs: holders
            .filter(holder => holder.userCurrentlyHoldsRole)
            .map(holder => holder.id),
    });

    useEffect(() => {
        if (postSuccess && !prevProps.postSuccess) {
            dispatch(fetchChallengeHoldersForGroup(challengeGroup.id));
            hideModal();
        }
        if (holders && !prevProps.holders) {
            handleChange('challengeHolderIDs', {
                challengeHolderIDs: holders
                    .filter(holder => holder.userCurrentlyHoldsRole)
                    .map(holder => holder.id),
            });
        }
    }, [postSuccess, prevProps, hideModal, holders, handleChange, challengeGroup.id, dispatch]);

    const handleSubmit = () => {
        dispatch(editChallengeHolder(challengeGroup.id, formState));
    };

    // We use spread operator here to copy (we dont want the readonly version)
    const holderOptions = [...holders]
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
        .map(holder => ({
            label: holder.firstName + ' ' + holder.lastName,
            value: holder.id,
        }));

    return {
        isFetching,
        fetchError,
        formState,
        handleChange,
        handleSubmit,
        isPosting,
        postError,
        postSuccess,
        prevSuccess: prevProps.postSuccess,
        holderOptions,
    };
};

interface FormState {
    challengeHolderIDs: number[];
}

interface Hook {
    holderOptions: DropdownOption<number>[];
    isFetching: boolean;
    fetchError: string | null;
    isPosting: boolean;
    postError: string | null;
    postSuccess: boolean;
    prevSuccess: boolean;
    formState: FormState;
    handleChange: <T>(name: keyof FormState, value: T) => void;
    handleSubmit: () => void;
}

export default useEditChallengeHolder;
